import React, { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../contexts/ThemeContext";
import { useDirection } from "../../contexts/DirectionContext";
import { useLocation } from "react-router-dom";
import { SearchBar } from "../../components/search";
import { SearchResults } from "../../components/search";
import { FilterMenu } from "../../components/search/FilterMenu";
import { ActiveFilters } from "../../components/search/ActiveFilters";
import { SearchType, SearchFilters } from "../../types/search";
import { Content } from "../../types/content";
import { useSearch } from "../../hooks/useSearch";
import { useResponsiveView } from "../../hooks/useResponsiveView";
import { DEFAULT_SEARCH_TYPE } from "../../constants/search";
import { Icon } from "@iconify/react";
import { Drawer } from "../../components/common/Drawer";
import { useTopics } from "../../hooks/useTopics";
import { SearchProvider } from "../../contexts/SearchContext";
import { parseSearchParams } from "../../utils/api/searchUtils";
import PageHeader from "../../components/common/PageHeader";
import SEO from "../../components/common/SEO";

const defaultFilters: SearchFilters = {
  topics: [],
  duration: undefined,
  language: undefined,
  status: "public",
  sortBy: undefined,
  sortType: undefined,
};

export const SearchPage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const location = useLocation();
  const { topics, loading: topicsLoading } = useTopics();
  const {
    isMobile,
    showMobileFilters,
    toggleMobileFilters,
    closeMobileFilters,
  } = useResponsiveView();

  // Parse initial URL parameters
  const urlParams = parseSearchParams(location.search);
  
  const [contentType, setContentType] = useState<SearchType>(urlParams.contentType || DEFAULT_SEARCH_TYPE);
  const [draftFilters, setDraftFilters] = useState<SearchFilters>(urlParams.filters || defaultFilters);

  const {
    results,
    loading,
    error,
    term,
    filters,
    hasMore,
    total,
    page,
    search,
    loadMore,
    updateFilters,
    setPage,
  } = useSearch();

  const resultsHeaderRef = useRef<HTMLDivElement>(null);

  // Initialize search from URL parameters
  useEffect(() => {
    const { term, contentType: urlContentType, filters: urlFilters } = parseSearchParams(location.search);
    
    const hasActiveFilters = Object.entries(urlFilters).some(([_, value]) => 
      Array.isArray(value) ? value.length > 0 : value !== undefined
    );
    
    if (term || urlContentType !== DEFAULT_SEARCH_TYPE || hasActiveFilters) {
      setContentType(urlContentType);
      search(term, urlContentType, urlFilters);
    }
  }, []);

  // Keep draft filters in sync with actual filters
  useEffect(() => {
    setDraftFilters(filters);
  }, [filters]);

  const handleSearch = useCallback(
    (params: { query: string; type: SearchType }) => {
      setContentType(params.type);
      search(params.query, params.type, draftFilters);
    },
    [search, draftFilters]
  );

  const handleContentTypeChange = useCallback((type: SearchType) => {
    setContentType(type);
    search(term, type, filters);
  }, [search, term, filters]);

  const handleFilterChange = useCallback((newFilters: SearchFilters) => {
    setDraftFilters(newFilters);
  }, []);

  const handleScrollToResults = useCallback(() => {
    if (resultsHeaderRef.current) {
      const headerHeight = parseInt(getComputedStyle(document.documentElement)
        .getPropertyValue('--header-total-height')
        .trim() || '0', 10);
      
      const elementPosition = resultsHeaderRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  const handleApplyFilters = useCallback(() => {
    updateFilters(draftFilters);
    handleScrollToResults();
    closeMobileFilters();
  }, [draftFilters, updateFilters, closeMobileFilters, handleScrollToResults]);

  const handleResetFilters = useCallback(() => {
    const resetFilters = { ...defaultFilters };
    setDraftFilters(resetFilters);
    updateFilters(resetFilters);
    handleScrollToResults();
  }, [updateFilters, handleScrollToResults]);

  const handleRemoveFilter = useCallback(
    (filterId: string, value: string) => {
      const newFilters = { ...draftFilters };
      switch (filterId) {
        case "topics":
          newFilters.topics = draftFilters.topics.filter((t) => t !== value);
          break;
        case "duration":
          newFilters.duration = undefined;
          break;
        case "sort":
          newFilters.sortBy = defaultFilters.sortBy;
          newFilters.sortType = defaultFilters.sortType;
          break;
      }
      setDraftFilters(newFilters);
      search(term || "", contentType, newFilters);
    },
    [draftFilters, search, term, contentType]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage]
  );

  const hasResults = results?.length > 0;
  const hasActiveFilters =
    filters?.topics?.length > 0 ||
    filters?.duration ||
    filters?.sortBy !== defaultFilters.sortBy;

  const filterHeader = (
    <h2 className="text-text-primary font-medium flex items-center gap-2">
      <Icon icon="ph:sliders-thin" className="w-5 h-5" />
      {t("search.filters.title")}
    </h2>
  );

  const filterActions = (
    <div className="flex gap-3">
      <button
        onClick={handleResetFilters}
        className="flex-1 px-4 py-3 rounded-full border border-border-secondary text-sm font-medium text-text-secondary hover:bg-background-secondary bg-transparent transition-colors"
      >
        {t("common.reset")}
      </button>
      <button
        onClick={handleApplyFilters}
        className={`flex-1 px-4 py-3 rounded-full text-sm font-medium 
          hover:bg-primary-900 hover:font-bold 
          bg-primary-500 
          ${theme.mode === "dark" ? "text-text-primary" : "text-text-inverse"} 
          transition-colors
          `}
      >
        {t("common.apply")}
      </button>
    </div>
  );

  const FloatingFilterButton = () => (
    <button
      onClick={toggleMobileFilters}
      className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 
                 flex items-center gap-2 px-6 py-3 
                 bg-surface-primary text-text-primary
                 border border-border-primary
                 hover:bg-surface-secondary transition-colors
                 rounded-full shadow-lg backdrop-blur-sm"
    >
      <Icon icon="ph:sliders-thin" className="w-5 h-5" />
      <span className="text-sm font-medium">
        {t("search.filters.title")}
      </span>
    </button>
  );

  // SEO metadata based on search state
  const getSEOData = () => {
    if (term) {
      const searchTypeLabel = t(`search.format.${contentType}`);
      const topicsLabel = filters.topics.length > 0 
        ? ` - ${filters.topics.join(', ')}` 
        : '';
      
      return {
        title: `${term} - ${searchTypeLabel}${topicsLabel}`,
        description: t('search.subtitle'),
        keywords: [
          ...filters.topics,
          'Jewish content',
          'Torah content',
          'Jewish search',
          contentType,
          term
        ]
      };
    }

    return {
      title: t('navigation.content'),
      description: t('search.subtitle'),
      keywords: [
        'Jewish content',
        'Torah content',
        'Jewish videos',
        'Jewish education'
      ]
    };
  };

  const seoData = getSEOData();

  return (
    <SearchProvider initialType={contentType}>
      <div className="flex flex-col min-h-screen bg-background-primary">
        <SEO
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
        />
        <PageHeader
          title={t("search.title")}
          description={t("search.subtitle")}
        >
          <div className="max-w-2xl mx-auto">
            <SearchBar
              value={term || ""}
              onSearch={handleSearch}
              contentType={contentType}
              onContentTypeChange={handleContentTypeChange}
              size="lg"
            />
          </div>
        </PageHeader>

        {/* Main Content */}
        <main className="flex-1">
          <div className="px-4 lg:px-8 xl:px-16 py-8 w-full">
            <div className="flex gap-8">
              {/* Desktop Filters */}
              {!isMobile && (
                <aside className="w-80 flex-shrink-0">
                  <div className="sticky top-[calc(var(--header-total-height)_+_1rem)] bg-surface-primary border border-border-primary rounded-lg overflow-hidden">
                    <div className="p-4 border-b border-border-primary bg-surface-secondary">
                      {filterHeader}
                    </div>
                    <div className="flex flex-col">
                      <div className="p-4">
                        <FilterMenu
                          contentType={contentType}
                          filters={draftFilters}
                          topics={topics}
                          onChange={handleFilterChange}
                          onClose={() => {}}
                        />
                      </div>
                      <div className="p-4 border-t border-border-primary bg-surface-secondary">
                        {filterActions}
                      </div>
                    </div>
                  </div>
                </aside>
              )}

              {/* Results Section */}
              <div className="flex-1 min-w-0">
                {/* Results Count */}
                {hasResults && (
                  <div className="mb-4">
                    <p className="text-sm text-text-primary">
                      {t("search.resultsFound", { count: total })}
                    </p>
                  </div>
                )}

                {/* Active Filters Container */}
                {hasActiveFilters && (
                  <div 
                    ref={resultsHeaderRef}
                    className="bg-surface-primary border border-border-primary rounded-lg mb-6 p-4"
                  >
                    <ActiveFilters
                      filters={filters}
                      topics={topics}
                      onRemove={handleRemoveFilter}
                    />
                  </div>
                )}

                {/* Results List */}
                <SearchResults
                  results={results || []}
                  loading={loading}
                  error={error}
                  hasMore={hasMore}
                  onLoadMore={loadMore}
                  total={total}
                  page={page}
                  onPageChange={handlePageChange}
                  term={term}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Mobile Filters Drawer */}
        <Drawer
          isOpen={isMobile && showMobileFilters}
          onClose={closeMobileFilters}
          header={filterHeader}
          footer={isMobile ? filterActions : undefined}
        >
          <FilterMenu
            contentType={contentType}
            filters={draftFilters}
            topics={topics}
            onChange={handleFilterChange}
            onClose={closeMobileFilters}
          />
        </Drawer>

        {/* Floating Filter Button for Mobile */}
        {isMobile && <FloatingFilterButton />}
      </div>
    </SearchProvider>
  );
};
